import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react'
import Checkbox from 'components/Checkbox'
import Button from 'components/Button'
import Select from 'components/Select'
import ItemsPerPageDropdown from 'components/ItemsPerPageDropdown'
import Pagination from 'features/Pagination'
import Tooltip from 'components/Tooltip'
import InfoIcon from 'assets/icons/info-icon'
import { FormattedMessage } from 'react-intl'
import Accordion from '../component/Accordion'
import EcosystemAccordionContent from './EcosystemAccordionContent'
import EcoSystemAccordionHeader from './EcosystemAccordionHeader'
import {
  PaginationWrapper
} from 'components/BiodiversitySelector/style'
import postRequest from 'utils/postRequest'
import {
  SpeciesList,
  BulkActionsWrapper
} from './style'
import { siteThreatsTiming } from 'constants/siteThreatsTiming'
import {
  requestThreatScope,
  requestThreatSeverity,
  requestDeleteSpeciesThreat,
  requestPressureCopyPaste,
  requestDeleteMonitorThreatTriggers,
  requestGetSitePressureSpecies
} from 'api/monitoring'
import { getCopyPayload, setPasteUsed, resetStore } from './ecosystemCopyStore'
import { useParams } from 'react-router-dom'
import { showErrorToast, showSuccessToast } from 'utils/toast'
import { PageSubtitle, TooltipHintWrapper, PageSubtitleWrapper } from 'flow/SiteProposal/pages/About/style'
import Modal from '../component/Modal'
import FlowRequestModal from 'components/FlowRequestModal'
import Container from 'components/Container'
const displayOptions = [
  { label: 'Taxon Group & Species', value: 'all' },
  { label: 'Species', value: 'species' }
]
const bulkActionOptions = [
  { label: 'Paste analysis', value: 'bulk-paste' },
  { label: 'Delete', value: 'bulk-delete' }
]
/* Keep your original filter that preserves hierarchy */
const flattenTree = (nodes, filter, depth = 0) => {
  return nodes.flatMap((node) => {
    const matchesFilter = filter === 'all' || node.kind === filter
    const children = node.children ? flattenTree(node.children, filter, depth + 1) : []
    const includeNode = matchesFilter || children.length
    if (!includeNode) {
      return []
    }
    return [
      {
        ...node,
        children,
        depth
      }
    ]
  })
}
/* helpers for building lookup maps and collecting ids */
const buildMaps = (nodes) => {
  const idToNode = new Map()
  const idToParent = new Map()
  const walk = (list, parent = null) => {
    list.forEach((node) => {
      idToNode.set(node.id, node)
      if (parent) idToParent.set(node.id, parent.id)
      if (node.children && node.children.length) walk(node.children, node)
    })
  }
  walk(nodes, null)
  return { idToNode, idToParent }
}
const collectDescendantIds = (node) => {
  const ids = []
  const walk = (n) => {
    ids.push(n.id)
    if (n.children && n.children.length) {
      n.children.forEach((c) => walk(c))
    }
  }
  walk(node)
  return ids
}
const collectAncestorIds = (id, idToParent) => {
  const ancestors = []
  let current = idToParent.get(id)
  while (current) {
    ancestors.push(current)
    current = idToParent.get(current)
  }
  return ancestors
}
const SpeciesTabContent = ({ preselectedItems, threatItemId, isEditable, isScopeOptions, isSeverityOptions, isTaxonScopeOptions, isTaxonSeverityOptions }) => {
  const params = useParams()
  const [typeFilter, setTypeFilter] = useState(displayOptions[0])
  const [bulkAction, setBulkAction] = useState(null)
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [pagination, setPagination] = useState(() => {
    const p = preselectedItems && preselectedItems.pagination ? preselectedItems.pagination : null
    const currentPage = p ? Number(p.currentPage ?? p.current_page ?? p.page ?? 1) : 1
    const resultsPerPage = p ? Number(p.resultsPerPage ?? p.results_per_page ?? p.perPage ?? p.limit ?? 12) : 12
    return { currentPage, resultsPerPage }
  })
  const [scopeSelections, setScopeSelections] = useState({})
  const [severitySelections, setSeveritySelections] = useState({})
  const [timingSelections, setTimingSelections] = useState({})
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [detailsTarget, setDetailsTarget] = useState(null)
  const [isTimingOptions, setIsTimingOptions] = useState([])
  const [removedNodeIds, setRemovedNodeIds] = useState(new Set())
  const [expandedSpeciesId, setExpandedSpeciesId] = useState(null)
  const [subTab, setSubTab] = useState('species')
  const [isModalForBulkDeletingOpen, setIsModalForBulkDeletingOpen] = useState(false)
  const [allSpecies, setAllSpecies] = useState([])
  const threatId = threatItemId?.threatItemId ?? null

  useEffect(() => {
    const timingSource = Array.isArray(siteThreatsTiming) ? siteThreatsTiming : []
    const formattedTimingOptions = timingSource.map((item) => ({ value: item.id, label: item.name }))
    setIsTimingOptions(formattedTimingOptions)
    // Fetch all species if preselectedItems is empty
    const isPreselectedEmpty = !preselectedItems ||
      (Array.isArray(preselectedItems) && preselectedItems.length === 0) ||
      (preselectedItems.data && preselectedItems.data.length === 0)
    if (isPreselectedEmpty) {
      const getMonitorThreatId = Number(threatItemId || params.monitorThreatId)
      requestGetSitePressureSpecies(params.id, params.monitoringId, getMonitorThreatId, pagination.currentPage, pagination.resultsPerPage).then(data => {
        setAllSpecies(data)
      }).catch(err => console.error('Failed to fetch all species', err))
    }
  }, [preselectedItems, params.id])
  const filteredTree = useMemo(() => {
    // normalize incoming preselected payloads: support array form or { data: [...] } form
    let sourceData = preselectedItems
    const isPreselectedEmpty = !sourceData ||
      (Array.isArray(sourceData) && sourceData.length === 0) ||
      (sourceData && sourceData.data && sourceData.data.length === 0)
    if (isPreselectedEmpty && Array.isArray(allSpecies) && allSpecies.length > 0) {
      // Group allSpecies by taxonomicGroup
      const grouped = {}
      allSpecies.forEach(sp => {
        const tax = sp.taxonomicGroup || 'Unknown'
        if (!grouped[tax]) {
          grouped[tax] = {
            taxonomicGroup: tax,
            species: [],
            raw: {} // No persisted raw for API-fetched groups
          }
        }
        grouped[tax].species.push(sp)
      })
      sourceData = { data: Object.values(grouped) }
    }
    let finalPreselectedItems = []
    if (Array.isArray(sourceData)) {
      finalPreselectedItems = sourceData
    } else if (sourceData && Array.isArray(sourceData.data)) {
      finalPreselectedItems = sourceData.data
    } else {
      finalPreselectedItems = []
    }
    const sourceNodes =
      finalPreselectedItems && Array.isArray(finalPreselectedItems)
        ? finalPreselectedItems
            // Sort taxon groups alphabetically
            .sort((a, b) => (a.taxonomicGroup || '').localeCompare(b.taxonomicGroup || ''))
            .map((grp, gi) => {
              // Count map to detect duplicates inside species
              const speciesCountMap = {}
              return {
                id: `group-${grp.taxonomicGroup || gi}`,
                name: grp.taxonomicGroup || `Group ${gi + 1}`,
                type: 'Taxon Group',
                kind: 'group',
                key: `group-${gi}`,
                raw: grp?.group_threats_triggers?.[0] || {},
                isChecked: false,
                monitorThreatId: preselectedItems?.threatItemId || null,
                children: Array.isArray(grp.species)
                  ? grp.species
                      // Sort species within group alphabetically
                      .sort((a, b) => (a.scientificName || a.name || '').localeCompare(b.scientificName || b.name || ''))
                      .map((s, si) => {
                        const baseId = s.id || s.scientificName || `species-${si}`
                        // Count duplicates
                        speciesCountMap[baseId] = (speciesCountMap[baseId] || 0) + 1
                        const dupIndex = speciesCountMap[baseId]
                        return {
                          id: `species-${baseId}-${dupIndex}`,
                          name: s.scientificName || s.name || `Species ${si + 1}`,
                          commonName: s.specieName,
                          type: 'Species',
                          kind: 'species',
                          key: `species-key-${baseId}-${dupIndex}`,
                          raw: s
                        }
                      })
                  : []
              }
            })
        : []
    return flattenTree(sourceNodes, typeFilter.value)
  }, [typeFilter, preselectedItems, allSpecies])
  /* build fast lookup maps from the filtered hierarchical tree */
  const { idToNode, idToParent } = useMemo(() => buildMaps(filteredTree), [filteredTree])
  useEffect(() => {
    if (!idToNode || idToNode.size === 0) return
    const threatIdToMatch = preselectedItems?.threatItemId?.threatItemId
      ? Number(preselectedItems?.threatItemId?.threatItemId)
      : null
    const newScope = {}
    const newSeverity = {}
    const newTiming = {}
    for (const [nodeId, node] of idToNode.entries()) {
      if (!node || !node.raw) continue
      const raw = node.raw || {}
      // support multiple possible field names and array/object shapes
      const triggers =
        raw.monitor_threats_trigger ||
        raw.monitor_threats_triggers ||
        raw.monitorThreatsTrigger ||
        raw.monitorThreatsTriggers ||
        raw.trigger ||
        raw.triggers
      if (!triggers) continue
      const list = Array.isArray(triggers) ? triggers : [triggers]
      // try to find the trigger that matches the current threat id (monitor_threats_id)
      let match = null
      if (threatIdToMatch != null) {
        match = list.find((t) => {
          const mtid = t && (t.monitor_threats_id || t.monitorThreatsId || t.monitor_threats_id || t.monitorThreatsId)
          return mtid != null && Number(mtid) === threatIdToMatch
        })
      }
      if (!match) match = list[0]
      if (!match) continue
      if (match.scope_id !== undefined) newScope[nodeId] = match.scope_id
      if (match.severity_id !== undefined) newSeverity[nodeId] = match.severity_id
      if (match.site_threat_timing_id !== undefined && match.site_threat_timing_id !== null)
        newTiming[nodeId] = match.site_threat_timing_id
      const returnedTriggerId =
        match.id ||
        match.monitor_threats_triggers_id ||
        match.monitorThreatsTriggersId ||
        match.triggerId ||
        match.monitor_threats_triggers_id
      if (returnedTriggerId) {
        node.raw = { ...(node.raw || {}), monitor_threats_triggers_id: returnedTriggerId }
      }
    }
    // seed selection states so UI reflects existing trigger data
    if (Object.keys(newScope).length > 0) setScopeSelections((prev) => ({ ...newScope }))
    if (Object.keys(newSeverity).length > 0) setSeveritySelections((prev) => ({ ...newSeverity }))
    if (Object.keys(newTiming).length > 0) setTimingSelections((prev) => ({ ...newTiming }))
  }, [idToNode, preselectedItems])
  /* compute full id list across filtered tree (every node, including nested children) */
  const allIds = useMemo(() => {
    const ids = []
    const walk = (nodes) => {
      nodes.forEach((n) => {
        ids.push(n.id)
        if (n.children && n.children.length) walk(n.children)
      })
    }
    walk(filteredTree)
    return ids
  }, [filteredTree])
  const roots = filteredTree
  /* If filter is 'species', present a flat list of species (no group parents) */
  const getSpeciesRoots = (nodes) => {
    const results = []
    const walk = (list) => {
      list.forEach((n) => {
        if (n.kind === 'species') results.push({ ...n, depth: 0 })
        if (n.children && n.children.length) walk(n.children)
      })
    }
    walk(nodes)
    return results
  }
  const effectiveRoots = typeFilter && typeFilter.value === 'species' ? getSpeciesRoots(filteredTree) : roots
  // compute total species count across the filtered tree so pagination reflects species totals
  const speciesCount = useMemo(() => {
    let count = 0
    const walk = (list) => {
      list.forEach((n) => {
        if (n.kind === 'species') count += 1
        if (n.children && n.children.length) walk(n.children)
      })
    }
    walk(filteredTree)
    return count
  }, [filteredTree])
  // count how many species have been removed locally so we can adjust pagination
  const removedSpeciesCount = useMemo(() => {
    if (!removedNodeIds || removedNodeIds.size === 0) return 0
    let removed = 0
    const walk = (list) => {
      list.forEach((n) => {
        if (n.kind === 'species' && removedNodeIds.has(n.id)) removed += 1
        if (n.children && n.children.length) walk(n.children)
      })
    }
    walk(filteredTree)
    return removed
  }, [filteredTree, removedNodeIds])
  const serverTotal =
    preselectedItems && preselectedItems.pagination
      ? (preselectedItems.pagination.total_results ??
        preselectedItems.pagination.total_results_count ??
        preselectedItems.pagination.total ??
        preselectedItems.pagination.totalItems ??
        preselectedItems.pagination.total_count)
      : null
  // If server reported a total, reduce it by locally removed species so UI pagination reflects deletions.
  // Otherwise use the visible species count after removals.
  const visibleSpeciesCount = Math.max(0, speciesCount - removedSpeciesCount)
  const totalItems =
    serverTotal !== null && serverTotal !== undefined
      ? Math.max(0, Number(serverTotal) - removedSpeciesCount)
      : visibleSpeciesCount
  const totalPages = Math.max(1, Math.ceil(totalItems / (pagination.resultsPerPage || 1)))
  // If server-side pagination is provided (preselectedItems.pagination exists),
  const serverPaginationProvided = (() => {
    if (!preselectedItems || !preselectedItems.pagination) return false
    const p = preselectedItems.pagination
    const serverTotal = Number(p.total_results ?? p.total_results_count ?? p.total ?? p.totalItems ?? p.total_count ?? 0)
    return serverTotal > speciesCount
  })()
  // When client-side pagination is used, paginate by SPECIES (not by taxon/group).
  const pageItems = (() => {
    if (serverPaginationProvided) return effectiveRoots
    const perPage = pagination.resultsPerPage || 1
    const page = Math.max(1, pagination.currentPage || 1)
    const start = (page - 1) * perPage
    const end = page * perPage
    // collect all species nodes in order, keeping reference to their parent group id
    const speciesList = []
    const walk = (nodes, parent = null) => {
      nodes.forEach((n) => {
        if (!n) return
        if (removedNodeIds && removedNodeIds.has(n.id)) return
        if (n.kind === 'species') {
          speciesList.push({ node: n, parent })
        } else if (Array.isArray(n.children) && n.children.length) {
          // if the group itself was removed, skip its children
          if (removedNodeIds && removedNodeIds.has(n.id)) return
          walk(n.children, n)
        }
      })
    }
    walk(effectiveRoots, null)
    const pageSpecies = speciesList.slice(start, end)
    const speciesIdsOnPage = new Set(pageSpecies.map((s) => s.node.id))
    // reconstruct roots but only include species that are on this page
    const rebuild = (nodes) => {
      return nodes
        .map((n) => {
          if (!n) return null
          if (n.kind === 'species') {
            if (removedNodeIds && removedNodeIds.has(n.id)) return null
            return speciesIdsOnPage.has(n.id) ? n : null
          }
          // group
          if (removedNodeIds && removedNodeIds.has(n.id)) return null
          const children = Array.isArray(n.children) ? rebuild(n.children).filter(Boolean) : []
          if (children.length === 0) return null
          return { ...n, children }
        })
        .filter(Boolean)
    }
    return rebuild(effectiveRoots)
  })()
  const filteredPageItems = useMemo(() => {
    if (!removedNodeIds || removedNodeIds.size === 0) return pageItems
    return pageItems
      .map((root) => {
        if (removedNodeIds.has(root.id)) return null
        const children = Array.isArray(root.children)
          ? root.children.filter((c) => !removedNodeIds.has(c.id))
          : root.children
        // if group root has no remaining children, drop it
        if (Array.isArray(children) && children.length === 0 && root.kind === 'group') return null
        return { ...root, children }
      })
      .filter(Boolean)
  }, [pageItems, removedNodeIds])
  // full filtered items WITHOUT pagination (used for header props)
  const filteredAllItems = useMemo(() => {
    if (!removedNodeIds || removedNodeIds.size === 0) return effectiveRoots
    return effectiveRoots
      .map((root) => {
        if (removedNodeIds.has(root.id)) return null
        const children = Array.isArray(root.children)
          ? root.children.filter((c) => !removedNodeIds.has(c.id))
          : root.children
        if (Array.isArray(children) && children.length === 0 && root.kind === 'group') return null
        return { ...root, children }
      })
      .filter(Boolean)
  }, [effectiveRoots, removedNodeIds])
  const handleToggleExpandedSpecies = useCallback((speciesId) => {
    setExpandedSpeciesId((currentExpandedSpeciesId) => {
      if (currentExpandedSpeciesId === speciesId) {
        return null
      }
      return speciesId
    })
  }, [])
  useEffect(() => {
    if (!expandedSpeciesId) return
    const speciesExistsOnCurrentPage = (() => {
      const walk = (nodes) => {
        return nodes.some((node) => {
          if (!node) return false
          if (node.id === expandedSpeciesId) return true
          if (Array.isArray(node.children) && node.children.length > 0) {
            return walk(node.children)
          }
          return false
        })
      }
      return walk(filteredPageItems)
    })()
    if (!speciesExistsOnCurrentPage) {
      setExpandedSpeciesId(null)
    }
  }, [expandedSpeciesId, filteredPageItems])
  // If the current page has no items (e.g., because pagination points to a now-empty page),
  // reset to page 1 so items become visible without a reload.
  useEffect(() => {
    try {
      if (!pagination) return
      const len = Array.isArray(filteredPageItems) ? filteredPageItems.length : 0
      if (len === 0 && pagination.currentPage > 1) {
        const nextPage = 1
        setPagination((prev) => ({ ...prev, currentPage: nextPage }))
        if (serverPaginationProvided) {
          try {
            document.dispatchEvent(
              new CustomEvent('species:paginationChanged', {
                detail: { currentPage: nextPage, resultsPerPage: pagination.resultsPerPage }
              })
            )
          } catch (e) {}
        }
      }
    } catch (e) {
      // ignore
    }
  }, [filteredPageItems, pagination && pagination.currentPage, serverPaginationProvided])
  // This handles the case where user deleted everything, then added new groups/species
  useEffect(() => {
    try {
      if (!filteredTree || filteredTree.length === 0) return
      if (!removedNodeIds || removedNodeIds.size === 0) return
      const presentNodeIds = new Set()
      const presentSpeciesBaseIds = new Set()
      const walk = (nodes) => {
        nodes.forEach((n) => {
          if (!n) return
          presentNodeIds.add(n.id)
          if (n.kind === 'species') {
            const m = String(n.id).match(/^species-(.+?)-?(\d+)?$/)
            if (m && m[1]) presentSpeciesBaseIds.add(m[1])
            const raw = n.raw || {}
            const candidates = []
            if (raw.historyId) candidates.push(String(raw.historyId))
            if (raw.SiteHistoryId) candidates.push(String(raw.SiteHistoryId))
            if (raw.id) candidates.push(String(raw.id))
            if (raw.siteSpeciesId) candidates.push(String(raw.siteSpeciesId))
            if (raw.site_species_id) candidates.push(String(raw.site_species_id))
            if (raw.scientificName) candidates.push(String(raw.scientificName))
            if (raw.name) candidates.push(String(raw.name))
            candidates.forEach((c) => presentSpeciesBaseIds.add(c))
          }
          if (n.children && n.children.length) walk(n.children)
        })
      }
      walk(filteredTree)
      let changed = false
      const next = new Set()
      for (const id of removedNodeIds) {
        if (!id) continue
        if (presentNodeIds.has(id)) { changed = true; continue }
        if (id.startsWith('species-')) {
          const m = String(id).match(/^species-(.+?)-?(\d+)?$/)
          if (m && m[1] && presentSpeciesBaseIds.has(m[1])) { changed = true; continue }
        }
        if (presentSpeciesBaseIds.has(id)) { changed = true; continue }
        next.add(id)
      }
      if (changed) {
        setRemovedNodeIds(next)
      }
    } catch (e) {
      // ignore
    }
  }, [filteredTree])
  // timing selections mapped for the full (non-paginated) filtered tree
  const timingAllSelections = useMemo(() => {
    const map = {}
    if (!filteredAllItems || filteredAllItems.length === 0) return map
    const walk = (nodes) => {
      nodes.forEach((n) => {
        if (!n) return
        const val = Object.prototype.hasOwnProperty.call(timingSelections, n.id)
          ? timingSelections[n.id]
          : n.raw && (n.raw.site_threat_timing_id || n.raw.siteThreatTimingId || null)
        if (val !== undefined && val !== null) map[n.id] = val
        if (Array.isArray(n.children) && n.children.length) walk(n.children)
      })
    }
    walk(filteredAllItems)
    return map
  }, [filteredAllItems, timingSelections])
  // guard to ensure we apply timing to each group only once per incoming payload
  const timingAppliedGroupsRef = useRef(new Set())
  // snapshot of node provenance keys that were present when component first loaded
  const initialNodeKeysRef = useRef(new Set())
  // flag to ensure timing application runs only once per preselectedItems change
  const hasAppliedTimingRef = useRef(false)
  // when incoming preselected items change (e.g., after Add more triggers),
  // clear the applied set so new groups will be processed
  useEffect(() => {
    try {
      timingAppliedGroupsRef.current.clear()
      hasAppliedTimingRef.current = false
    } catch (e) {}
  }, [preselectedItems])
  // populate initial snapshot of existing nodes (provenance keys) once
  useEffect(() => {
    try {
      if (initialNodeKeysRef.current.size === 0 && idToNode && typeof idToNode.entries === 'function') {
        for (const [nid, node] of idToNode.entries()) {
          if (!node) continue
          const r = node.raw || {}
          const key = r.monitor_threats_triggers_id || r.historyId || r.SiteHistoryId || r.siteSpeciesId || r.site_species_id || r.id || nid
          if (key) initialNodeKeysRef.current.add(String(key))
        }
      }
    } catch (e) {
      // ignore snapshot failures
    }
  }, [idToNode])
  /* helpers to select/deselect descendants */
  const selectNodeAndDescendants = useCallback(
    (nodeId, set) => {
      const node = idToNode.get(nodeId)
      if (!node) return
      const desc = collectDescendantIds(node)
      desc.forEach((id) => set.add(id))
    },
    [idToNode]
  )
  const deselectNodeAndDescendants = useCallback(
    (nodeId, set) => {
      const node = idToNode.get(nodeId)
      if (!node) return
      const desc = collectDescendantIds(node)
      desc.forEach((id) => set.delete(id))
    },
    [idToNode]
  )
  /* is All selected? depends on allIds length */
  const isAllSelected = useMemo(() => allIds.length > 0 && selectedIds.size === allIds.length, [allIds, selectedIds])
  const handleSelectAll = useCallback(() => {
    if (isAllSelected) {
      setSelectedIds(new Set())
      return
    }
    setSelectedIds(new Set(allIds))
  }, [isAllSelected, allIds])
  /* main toggle handler used by header & content checkboxes */
  const handleToggleNode = useCallback(
    (node) => {
      const nodeId = node.id
      const next = new Set(selectedIds)
      const currentlySelected = next.has(nodeId)
      if (!currentlySelected) {
        // select node + descendants
        selectNodeAndDescendants(nodeId, next)
        // ensure ancestors are selected only when all their species descendants are selected
        const ancestors = collectAncestorIds(nodeId, idToParent)
        ancestors.forEach((anc) => {
          const ancNode = idToNode.get(anc)
          if (!ancNode) return
          const desc = collectDescendantIds(ancNode)
          // consider only species descendants when deciding if ancestor should be selected
          const speciesDesc = desc.filter((id) => {
            const n = idToNode.get(id)
            return n ? n.kind === 'species' : true
          })
          const allSelected = speciesDesc.length > 0 && speciesDesc.every((id) => next.has(id))
          if (allSelected) next.add(anc)
        })
      } else {
        // deselect node + descendants
        deselectNodeAndDescendants(nodeId, next)
        // for each ancestor: if none of its descendants remain selected, remove ancestor
        const ancestors = collectAncestorIds(nodeId, idToParent)
        ancestors.forEach((anc) => {
          const ancNode = idToNode.get(anc)
          if (!ancNode) return
          const ancDesc = collectDescendantIds(ancNode)
          const anySelected = ancDesc.some((id) => next.has(id))
          if (!anySelected) next.delete(anc)
        })
      }
      setSelectedIds(next)
    },
    [selectedIds, idToNode, idToParent, selectNodeAndDescendants, deselectNodeAndDescendants]
  )
  const updateScope = useCallback(
    (nodeId, value) => {
      setScopeSelections((prev) => ({ ...prev, [nodeId]: value }))
      const node = idToNode.get(nodeId)
      if (!node) return
      const numeric = value && typeof value === 'object' && value.value !== undefined ? value.value : value
      const raw = node.raw || {}
      const monitorId = Number((threatItemId && (threatItemId.threatItemId ?? threatItemId)) || threatItemId || null)
      const body = [
        {
          monitor_threats_id: monitorId || null,
          siteSpeciesId: raw.historyId || raw.siteSpeciesId || raw.site_species_id || null,
          scope_id: numeric,
          common_name: node.commonName || "",
          site_pressure_id: Number(params.pressureId) || null
        }
      ]
      if (monitorId) {
        postRequest(`/api/monitor-threats-triggers`, body)
          .then((resp) => {
            try {
              const created = Array.isArray(resp) ? resp[0] : resp
              const returnedId =
                created &&
                (created.monitor_threats_triggers_id ||
                  created.monitorThreatsTriggersId ||
                  created.id ||
                  created.monitor_threats_triggers_id)
              if (returnedId) {
                node.raw = { ...(node.raw || {}), monitor_threats_triggers_id: returnedId }
              }
            } catch (err) {
              console.error('Failed to persist created trigger id', err)
            }
          })
          .catch((e) => console.error(e))
      }
    },
    [idToNode, preselectedItems]
  )
  const updateSeverity = useCallback(
    (nodeId, value) => {
      setSeveritySelections((prev) => ({ ...prev, [nodeId]: value }))
      const node = idToNode.get(nodeId)
      if (!node) return
      const numeric = value && typeof value === 'object' && value.value !== undefined ? value.value : value
      const raw = node.raw || {}
      const monitorId = Number((threatItemId && (threatItemId.threatItemId ?? threatItemId)) || threatItemId || null)
      const body = [
        {
          monitor_threats_id: monitorId || null,
          siteSpeciesId: raw.historyId || raw.siteSpeciesId || raw.site_species_id || null,
          severity_id: numeric,
          site_pressure_id: Number(params.pressureId) || null,
          common_name: node.commonName || ""
        }
      ]
      if (monitorId) {
        postRequest(`/api/monitor-threats-triggers`, body)
          .then((resp) => {
            try {
              const created = Array.isArray(resp) ? resp[0] : resp
              const returnedId =
                created &&
                (created.monitor_threats_triggers_id ||
                  created.monitorThreatsTriggersId ||
                  created.id ||
                  created.monitor_threats_triggers_id)
              if (returnedId) {
                node.raw = { ...(node.raw || {}), monitor_threats_triggers_id: returnedId }
              }
            } catch (err) {
              console.error('Failed to persist created trigger id', err)
            }
          })
          .catch((e) => console.error(e))
      }
    },
    [idToNode, preselectedItems]
  )
  const updateTiming = useCallback(
    (nodeId, option) => {
      setTimingSelections((prev) => ({ ...prev, [nodeId]: option }))
      const node = idToNode.get(nodeId)
      if (!node) return
      const numeric = option && typeof option === 'object' && option.value !== undefined ? option.value : option
      const raw = node.raw || {}
      const monitorId = Number((threatItemId && (threatItemId.threatItemId ?? threatItemId)) || threatItemId || null)
      const body = [
        {
          monitor_threats_id: monitorId || null,
          siteSpeciesId: raw.historyId || raw.siteSpeciesId || raw.site_species_id || null,
          site_threat_timing_id: numeric,
          site_pressure_id: Number(params.pressureId) || null,
          common_name: node.commonName || ""
        }
      ]
      if (monitorId) {
        postRequest(`/api/monitor-threats-triggers`, body)
          .then((resp) => {
            try {
              const created = Array.isArray(resp) ? resp[0] : resp
              const returnedId =
                created &&
                (created.monitor_threats_triggers_id ||
                  created.monitorThreatsTriggersId ||
                  created.id ||
                  created.monitor_threats_triggers_id)
              if (returnedId) {
                node.raw = { ...(node.raw || {}), monitor_threats_triggers_id: returnedId }
              }
            } catch (err) {
              console.error('Failed to persist created trigger id', err)
            }
          })
          .catch((e) => console.error(e))
      }
    },
    [idToNode, preselectedItems]
  )
  const openDetailsModal = useCallback(
    (item) => {
      setDetailsTarget(item)
      setIsDetailsModalOpen(true)
    },
    [idToNode, preselectedItems]
  )
  const closeDetailsModal = useCallback(() => {
    setIsDetailsModalOpen(false)
    setDetailsTarget(null)
  }, [idToNode, preselectedItems])
  const handleCloneNode = useCallback(() => {}, [])
  const handleDeleteNode = useCallback(
    (node) => {
      if (!node) return
      const raw = node.raw || {}
      // Attempt to derive the trigger id from several possible fields
      const kind = node.kind || (raw.kind ? String(raw.kind).toLowerCase() : null)
      let monitorThreatId = null;
      let triggerId = Number(raw.monitor_threats_triggers_id) || raw.monitorThreatsTriggersId || raw.triggerId || Number(raw.id)
      // Collect removed identifiers for parent component
      const removedGroupNames = []
      const removedSpeciesHistoryIds = []
      // If this is a group, collect group name
      if (node.kind === 'group') {
        monitorThreatId = raw.monitor_threats_id || null
        removedGroupNames.push(node.name)
      } else if (node.kind === 'species') {
         monitorThreatId = raw.monitor_threats_trigger?.[0]?.monitor_threats_id || null
        // If this is a species, collect its history ID
        const historyId = node.raw && (node.raw.historyId || node.raw.siteSpeciesId || node.raw.site_species_id || node.raw.id)
        if (historyId) {
          removedSpeciesHistoryIds.push(historyId)
        }
      }
      const removeLocally = () => {
        setRemovedNodeIds((prev) => {
          const next = new Set(prev)
          next.add(node.id)
          // also add descendants to removed set
          const desc = collectDescendantIds(node)
          desc.forEach((d) => next.add(d))
          return next
        })
        // remove node and descendants from selection to update UI
        setSelectedIds((prev) => {
          const next = new Set(prev)
          deselectNodeAndDescendants(node.id, next)
          // also remove the node itself if present
          next.delete(node.id)
          return next
        })
      }
      if (!triggerId) {
        // no server id yet -> hide locally
        removeLocally()
        return
      }
      // call API then remove locally on success
      requestDeleteSpeciesThreat(triggerId, monitorThreatId)
        .then(() => {
          removeLocally()
          try { showSuccessToast('Selected item deleted successfully.') } catch (e) {}
        })
        .catch((e) => {
          console.error('Failed to delete species threat', e)
        })
    },
    [deselectNodeAndDescendants, pagination]
  )
  // When external preselected items arrive, try to match them to nodes
  // in the current tree (by id first, then by name). If a match is found,
  // select the node + descendants and ensure its ancestors are selected too.
  useEffect(() => {
    if (!preselectedItems) return
    // derive a flat list of species objects from the incoming payload which may be:
    // - an object { data: [{ taxonomicGroup, species: [...] }, ...], pagination }
    // - an array (legacy) of species/group items
    let speciesList = []
    if (preselectedItems && Array.isArray(preselectedItems.data)) {
      speciesList = preselectedItems.data.flatMap((g) => (Array.isArray(g.species) ? g.species : []))
    } else if (Array.isArray(preselectedItems)) {
      // could be an array of species objects or nodes
      speciesList = preselectedItems
    }
    if (!speciesList || speciesList.length === 0) return
    if (!idToNode || idToNode.size === 0) return
    const next = new Set(selectedIds)
    speciesList.forEach((it) => {
      if (!it) return
      // try by id
      if (idToNode.has(it.id)) {
        const node = idToNode.get(it.id)
        const desc = collectDescendantIds(node)
        desc.forEach((d) => next.add(d))
        // add ancestors
        const ancestors = collectAncestorIds(it.id, idToParent)
        ancestors.forEach((a) => next.add(a))
        return
      }
      // fallback: try to match by name (case-insensitive)
      for (const [nodeId, node] of idToNode.entries()) {
        if (
          node &&
          node.name &&
          it.specieName &&
          String(node.name).toLowerCase() === String(it.specieName).toLowerCase()
        ) {
          const desc = collectDescendantIds(node)
          desc.forEach((d) => next.add(d))
          const ancestors = collectAncestorIds(nodeId, idToParent)
          ancestors.forEach((a) => next.add(a))
          break
        }
        if (node && node.name && it.name && String(node.name).toLowerCase() === String(it.name).toLowerCase()) {
          const desc = collectDescendantIds(node)
          desc.forEach((d) => next.add(d))
          const ancestors = collectAncestorIds(nodeId, idToParent)
          ancestors.forEach((a) => next.add(a))
          break
        }
      }
    })
    if (next.size > 0) {
      setSelectedIds(next)
    }
  }, [preselectedItems, idToNode, idToParent])
  // compute an accordion key to force remount when selection changes
  const accordionKey = useMemo(() => {
    let idsArr = []
    if (preselectedItems && Array.isArray(preselectedItems.data)) {
      idsArr = preselectedItems.data.flatMap((g) => (Array.isArray(g.species) ? g.species.map((s) => s.id) : []))
    } else if (Array.isArray(preselectedItems)) {
      idsArr = preselectedItems.map((i) => i && i.id).filter(Boolean)
    }
    const ids = idsArr.join(',')
    return `accordion-${ids}-${pagination.currentPage}-${typeFilter.value}`
  }, [preselectedItems, pagination.currentPage, typeFilter.value])
  // Keep local pagination in sync if the parent provides server-side pagination metadata
  useEffect(() => {
    if (!preselectedItems || !preselectedItems.pagination) return
    const p = preselectedItems.pagination
    const currentPage = Number(p.currentPage ?? p.current_page ?? p.page ?? 1)
    const resultsPerPage = Number(p.resultsPerPage ?? p.results_per_page ?? p.perPage ?? p.limit ?? 12)
    setPagination((prev) => ({ ...prev, currentPage, resultsPerPage }))
  }, [preselectedItems])
  // Clamp current page to the available totalPages to avoid empty results
  useEffect(() => {
    if (!pagination || !totalPages) return
    if (pagination.currentPage > totalPages) {
      setPagination((prev) => ({ ...prev, currentPage: totalPages }))
    }
  }, [totalPages])
  // No-op header used when in species-only flat view to avoid rendering
  // the per-item accordion header (we only want the content rows).
  const NoHeader = () => null
  //Taxonomic Group update scope of threat handler
  const updateTaxonGroupScope = useCallback(
    (nodeId, value) => {
      const node = idToNode.get(nodeId)
      if (!node) return
      const numeric = value && typeof value === 'object' && value.value !== undefined ? value.value : value
      // collect species nodes under this taxon-group so we can apply selection to them
      const speciesNodes = []
      const walk = (n) => {
        if (!n) return
        if (removedNodeIds && removedNodeIds.has(n.id)) return
        if (n.kind === 'species') {
          speciesNodes.push(n)
          return
        }
        if (Array.isArray(n.children)) n.children.forEach((c) => walk(c))
      }
      walk(node)
      // set scope selection for the group and for all descendant species
      setScopeSelections((prev) => {
        const next = { ...prev, [nodeId]: value }
        speciesNodes.forEach((s) => {
          if (value?.value === 999) {
            next[s.id] = null
          } else {
            next[s.id] = value
          }
        })
        return next
      })
      // Additionally, when updating a taxon group scope, call the taxon-group endpoint
      try {
        const speciesArray = speciesNodes
        .map((s) => {
          const id = (s.raw.historyId || s.raw.siteSpeciesId || s.raw.site_species_id) || s.id
          if (!id) return null

          return {
            id,
            common_name: s.specieName || s.commonName || ""
          }
        })
        .filter(Boolean)
        // const monitorId = Number(preselectedItems?.threatItemId?.threatItemId)
        const monitorId = Number(preselectedItems?.threatItemId) || Number(node.raw?.monitor_threats_id)
        if (monitorId && speciesArray.length > 0) {
          const payload = {
            monitor_threats_id: monitorId,
            scope_id: numeric,
            species: speciesArray,
            ecosystem_id: [],
            site_pressure_id: Number(params.pressureId) || null
          }
          postRequest(`/api/monitor-threats-triggers/${encodeURIComponent(node.name)}/taxongroup`, payload)
            .then(() => {
              // no-op: server handled taxon-group level update
            })
            .catch((err) => console.error('Failed taxon-group scope update', err))
        }
      } catch (err) {
        console.error('Error while preparing taxon-group payload', err)
      }
    },
    [idToNode, preselectedItems, removedNodeIds]
  )
  //Taxonomic Group update severity of threat handler
  const updateTaxonGroupSeverity = useCallback(
    (nodeId, value) => {
      const node = idToNode.get(nodeId)
      if (!node) return
      const numeric = value && typeof value === 'object' && value.value !== undefined ? value.value : value
      // collect species nodes under this node
      const speciesNodes = []
      const walk = (n) => {
        if (!n) return
        if (removedNodeIds && removedNodeIds.has(n.id)) return
        if (n.kind === 'species') {
          speciesNodes.push(n)
          return
        }
        if (Array.isArray(n.children)) n.children.forEach((c) => walk(c))
      }
      walk(node)
      // set severity selection for group and descendant species
      setSeveritySelections((prev) => {
        const next = { ...prev, [nodeId]: value }
        speciesNodes.forEach((s) => {
          if (value?.value === 999) {
            next[s.id] = null
          } else {
            next[s.id] = value
          }
        })
        return next
      })
      try {
        const speciesArray = speciesNodes
        .map((s) => {
          const id = (s.raw.historyId || s.raw.siteSpeciesId || s.raw.site_species_id) || s.id
          if (!id) return null

          return {
            id,
            common_name: s.specieName || s.commonName || ""
          }
        })
        .filter(Boolean)
        // const monitorId = Number(preselectedItems?.threatItemId?.threatItemId)
        const monitorId = Number(preselectedItems?.threatItemId) || Number(node.raw?.monitor_threats_id)
        if (monitorId && speciesArray.length > 0) {
          const payload = {
            monitor_threats_id: monitorId,
            severity_id: numeric,
            species: speciesArray,
            ecosystem_id: [],
            site_pressure_id: Number(params.pressureId) || null
          }
          postRequest(`/api/monitor-threats-triggers/${encodeURIComponent(node.name)}/taxongroup`, payload)
            .then(() => {
              // no-op: server handled taxon-group level update
            })
            .catch((err) => console.error('Failed taxon-group severity update', err))
        }
      } catch (err) {
        console.error('Error while preparing taxon-group payload', err)
      }
    },
    [idToNode, preselectedItems, removedNodeIds]
  )
  //Taxonomic Group update timing of threat handler
  const updateTaxonGroupTiming = useCallback(
    (nodeId, value) => {
      const node = idToNode.get(nodeId)
      if (!node) return
      const numeric = value && typeof value === 'object' && value.value !== undefined ? value.value : value
      // collect species nodes under this node
      const speciesNodes = []
      const walk = (n) => {
        if (!n) return
        if (removedNodeIds && removedNodeIds.has(n.id)) return
        if (n.kind === 'species') {
          speciesNodes.push(n)
          return
        }
        if (Array.isArray(n.children)) n.children.forEach((c) => walk(c))
      }
      walk(node)
      // set timing selection for group and descendant species
      setTimingSelections((prev) => {
        const next = { ...prev, [nodeId]: value }
        speciesNodes.forEach((s) => {
          next[s.id] = value
        })
        return next
      })
      try {
        const speciesArray = speciesNodes
        .map((s) => {
          const id = (s.raw.historyId || s.raw.siteSpeciesId || s.raw.site_species_id) || s.id
          if (!id) return null

          return {
            id,
            common_name: s.specieName || s.commonName || ""
          }
        })
        .filter(Boolean)
        // const monitorId = Number(preselectedItems?.threatItemId?.threatItemId)
        const monitorId = Number(preselectedItems?.threatItemId) || Number(node.raw?.monitor_threats_id)
        if (monitorId && speciesArray.length > 0) {
          const payload = {
            monitor_threats_id: monitorId,
            site_threat_timing_id: numeric,
            species: speciesArray,
            site_pressure_id: Number(params.pressureId) || null,
            ecosystem_id: []
          }
          postRequest(`/api/monitor-threats-triggers/${encodeURIComponent(node.name)}/taxongroup`, payload)
            .then(() => {
              // no-op: server handled taxon-group level update
            })
            .catch((err) => console.error('Failed taxon-group timing update', err))
        }
      } catch (err) {
        console.error('Error while preparing taxon-group payload', err)
      }
    },
    [idToNode, preselectedItems, removedNodeIds]
  )
  const bulkPasteHandler = (pastePayload) => {
    const checkedGroupNames = []
    const checkedSpeciesHistoryIds = []
    // Group is included only when ALL its species descendants are selected.
    try {
      if (idToNode && typeof idToNode.entries === 'function') {
        for (const [nid, node] of idToNode.entries()) {
          if (!node) continue
          // group: include only when ALL species descendants are selected
          if (node.kind === 'group') {
            const desc = collectDescendantIds(node)
            const speciesDesc = desc.filter((id) => {
              const n = idToNode.get(id)
              return n ? n.kind === 'species' : true
            })
            const allSelected = speciesDesc.length > 0 && speciesDesc.every((id) => selectedIds.has(id))
            if (allSelected) {
              checkedGroupNames.push(node.name)
              continue
            }
          }
          // species: include only when explicitly selected
          if (node.kind === 'species') {
            if (selectedIds && selectedIds.has(nid)) {
              const hist =
                (node.raw &&
                  (node.raw.historyId || node.raw.siteSpeciesId || node.raw.site_species_id || node.raw.id)) ||
                node.id
              checkedSpeciesHistoryIds.push(hist)
            }
          }
        }
      }
    } catch (e) {
      // ignore
    }
    if (bulkAction?.value === 'bulk-delete') {
      if (
        (!checkedGroupNames || checkedGroupNames.length === 0) &&
        (!checkedSpeciesHistoryIds || checkedSpeciesHistoryIds.length === 0)
      ) {
        showErrorToast(<FormattedMessage id="beforeNotSelectBulkItemMessage" />)
        return
      } else {
        setIsModalForBulkDeletingOpen(true)
        return
      }
    } else if (bulkAction?.value === 'bulk-paste') {
      if (
        (!checkedGroupNames || checkedGroupNames.length === 0) &&
        (!checkedSpeciesHistoryIds || checkedSpeciesHistoryIds.length === 0)
      ) {
        showErrorToast(<FormattedMessage id="triggerSpeciesNoSelectMessage" />)
        return
      }
      const payload = getCopyPayload()
      if (!payload || Object.keys(payload).length === 0) {
        showErrorToast(<FormattedMessage id="beforeNotSelectCopyPasteItemMessage" />)
        return
      }
      const finalPayload = {
        ...payload,
        paste_to_taxon: checkedGroupNames,
        paste_to_species: checkedSpeciesHistoryIds,
        site_id: params.id || null,
        filter_by: 1
      }
      // apply copy values to local selection states so UI reflects pasted values
      try {
        const scopeVal = payload && (payload.scope_id || payload.scope_id === 0) ? payload.scope_id : null
        const severityVal = payload && (payload.severity_id || payload.severity_id === 0) ? payload.severity_id : null
        const timingVal =
          payload && (payload.site_threat_timing_id || payload.site_threat_timing_id === 0)
            ? payload.site_threat_timing_id
            : null
        // update group + descendant species
        const newScope = { ...(scopeSelections || {}) }
        const newSeverity = { ...(severitySelections || {}) }
        const newTiming = { ...(timingSelections || {}) }
        if (idToNode && typeof idToNode.entries === 'function') {
          for (const [nid, node] of idToNode.entries()) {
            if (!node) continue
            // groups: check by name presence in checkedGroupNames
            if (node.kind === 'group' && checkedGroupNames.includes(node.name)) {
              if (scopeVal !== null) newScope[nid] = scopeVal
              if (severityVal !== null) newSeverity[nid] = severityVal
              if (timingVal !== null) newTiming[nid] = timingVal
              // also apply to descendant species
              const desc = collectDescendantIds(node)
              desc.forEach((d) => {
                const dn = idToNode.get(d)
                if (!dn) return
                if (dn.kind === 'species') {
                  if (scopeVal !== null) newScope[d] = scopeVal
                  if (severityVal !== null) newSeverity[d] = severityVal
                  if (timingVal !== null) newTiming[d] = timingVal
                }
              })
              continue
            }
            // species: match by historyId/site id or node id against checkedSpeciesHistoryIds
            if (node.kind === 'species') {
              const hist =
                (node.raw && (node.raw.historyId || node.raw.siteSpeciesId || node.raw.site_species_id || node.raw.id)) ||
                node.id
              if (checkedSpeciesHistoryIds.includes(hist) || checkedSpeciesHistoryIds.includes(node.id)) {
                if (scopeVal !== null) newScope[nid] = scopeVal
                if (severityVal !== null) newSeverity[nid] = severityVal
                if (timingVal !== null) newTiming[nid] = timingVal
              }
            }
          }
        }
        setScopeSelections(newScope)
        setSeveritySelections(newSeverity)
        setTimingSelections(newTiming)
      } catch (e) {
        // ignore local update failures
      }
      // remove unwanted keys from finalPayload before sending
      ;['scope_id', 'severity_id', 'site_threat_timing_id'].forEach((k) => {
        if (k in finalPayload) delete finalPayload[k]
      })
      //API call
      try {
        setPasteUsed(true)
      } catch (e) {}
      requestPressureCopyPaste(finalPayload)
        .then((response) => {
          //handle response
          showSuccessToast(<FormattedMessage id="pasteSuccessMessage" />)
        })
        .catch((error) => {
          showErrorToast(<FormattedMessage id="pasteFailureMessage" />)
          //handle error
          console.error('bulk handlePaste error: ', error)
        })
        .finally(() => {
          try {
            setPasteUsed(false)
          } catch (e) {}
        })
    }
  }
  async function bulkDeleteHandler() {
    // collect checked group names and species historyIds from current selection
    // Group is included only when ALL its species descendants are selected.
    const checkedGroupNames = []
    const checkedSpeciesHistoryIds = []
    try {
      if (idToNode && typeof idToNode.entries === 'function') {
        for (const [nid, node] of idToNode.entries()) {
          if (!node) continue
          // group: include only when ALL species descendants are selected
          if (node.kind === 'group') {
            const desc = collectDescendantIds(node)
            const speciesDesc = desc.filter((id) => {
              const n = idToNode.get(id)
              return n ? n.kind === 'species' : true
            })
            const allSelected = speciesDesc.length > 0 && speciesDesc.every((id) => selectedIds.has(id))
            if (allSelected) {
              checkedGroupNames.push(node.name)
              continue
            }
          }
          // species: include only when explicitly selected
          if (node.kind === 'species') {
            if (selectedIds && selectedIds.has(nid)) {
              const hist =
                (node.raw &&
                  (node.raw.historyId || node.raw.siteSpeciesId || node.raw.site_species_id || node.raw.id)) ||
                node.id
              checkedSpeciesHistoryIds.push(hist)
            }
          }
        }
      }
    } catch (e) {}
    if (bulkAction?.value === 'bulk-delete') {
      const finalPayload = {
        delete_taxon: checkedGroupNames,
        delete_species: checkedSpeciesHistoryIds,
        site_pressure_id: Number(params.pressureId) || null,
      }
      if (
        (!finalPayload.delete_taxon || finalPayload.delete_taxon.length === 0) &&
        (!finalPayload.delete_species || finalPayload.delete_species.length === 0)
      ) {
        showErrorToast(<FormattedMessage id="beforeNotSelectBulkItemMessage" />)
        return
      }
      try {
        const res = await requestDeleteMonitorThreatTriggers(threatItemId, finalPayload)
        setIsModalForBulkDeletingOpen(false);
        try { resetStore() } catch (e) {}
        try {
          document.dispatchEvent(
            new CustomEvent('ecosystem:buttonToggled', { detail: { scope: 'header', id: null, button: null } })
          )
        } catch (e) {}
        // mark removed nodes locally so UI updates immediately
        try {
          setRemovedNodeIds((prev) => {
            const next = new Set(prev)
            if (idToNode && typeof idToNode.entries === 'function') {
              for (const [nid, node] of idToNode.entries()) {
                if (!node) continue
                // group by name
                if (node.kind === 'group' && checkedGroupNames.includes(node.name)) {
                  next.add(nid)
                  const desc = collectDescendantIds(node)
                  desc.forEach((d) => next.add(d))
                  continue
                }
                if (node.kind === 'species') {
                  const hist = (node.raw && (node.raw.historyId || node.raw.siteSpeciesId || node.raw.site_species_id || node.raw.id)) || node.id
                  if (checkedSpeciesHistoryIds.includes(hist) || checkedSpeciesHistoryIds.includes(node.id)) {
                    next.add(nid)
                  }
                }
              }
            }
            return next
          })
        } catch (e) {}
        // clear current selection for deleted items
        try {
          setSelectedIds((prev) => {
            const next = new Set(prev)
            if (idToNode && typeof idToNode.entries === 'function') {
              for (const [nid, node] of idToNode.entries()) {
                if (!node) continue
                const hist = (node.raw && (node.raw.historyId || node.raw.siteSpeciesId || node.raw.site_species_id || node.raw.id)) || node.id
                if (checkedSpeciesHistoryIds.includes(hist) || checkedSpeciesHistoryIds.includes(node.id) || (node.kind === 'group' && checkedGroupNames.includes(node.name))) {
                  // remove node and its descendants from selection
                  const desc = collectDescendantIds(node)
                  desc.forEach((d) => next.delete(d))
                  next.delete(nid)
                }
              }
            }
            return next
          })
        } catch (e) {}
        showSuccessToast(<FormattedMessage id="bulkDeleteToasterConfirmationMessage" />)
        // notify parent to re-fetch (server-side pagination / authoritative list)
        try {
          document.dispatchEvent(
            new CustomEvent('species:refresh', {
              detail: { currentPage: pagination.currentPage, resultsPerPage: pagination.resultsPerPage }
            })
          )
        } catch (e) {}
      } catch (error) {
        showErrorToast(<FormattedMessage id="bulkDeleteToasterFailureMessage" />)
        setIsModalForBulkDeletingOpen(false);
        console.error('bulk delete error: ', error)
      }
    }
  }

  return (
    <div>
      {filteredPageItems.length > 0 && (
        <PageSubtitleWrapper style={{ marginTop: 20, justifyContent: 'flex-start', marginBottom: 0 }}>
          <PageSubtitle style={{ borderBottom: 'none', fontSize: '14px', fontWeight: 'normal' }}>
            <FormattedMessage id="threatTriggerSpecieSubTitle" />
          </PageSubtitle>
          <Tooltip
          placement="bottom"
          trigger={["hover"]}
          align={{ offset: [0, 16] }}
          zIndex={9999}
          overlay={{
            label: "threatTriggerSpecieSubTitle",
            infoTooltip: "threatTriggerSpecieInfoTooltip",
          }}
          type="big"
        >
          <TooltipHintWrapper>
            <InfoIcon />
          </TooltipHintWrapper>
          </Tooltip>
        </PageSubtitleWrapper>
      )}
      {filteredPageItems.length > 0 && (
        <BulkActionsWrapper>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                label={<FormattedMessage id="selectAll" defaultMessage="selectAll" />}
                checked={isAllSelected}
                onChange={handleSelectAll}
                disabled={!isEditable}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Select
                className="bulk-select"
                marginBottom="0"
                value={bulkAction}
                onChange={(option) => setBulkAction(option)}
                placeholder="bulkAction"
                options={bulkActionOptions}
                isDisabled={!isEditable}
                disabled={!isEditable}
              />
              <Button marginTop="0" type={!bulkAction || !isEditable ? 'disabledPrimary' : 'primary'} isDisabled={!bulkAction || !isEditable} onClick={() => bulkPasteHandler('asda')}>
                <FormattedMessage id="apply" defaultMessage="apply" />
              </Button>
            </div>
          </div>
        </BulkActionsWrapper>
      )}
      <SpeciesList>
        {!preselectedItems.data || preselectedItems?.data.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#666' }}>
            <FormattedMessage id="noRecordsFound" defaultMessage="noRecordsFound" />
          </div>
        ) : (
          <Accordion
            key={accordionKey}
            items={filteredPageItems}
            headerComponent={EcoSystemAccordionHeader}
            headerProps={{
              scopeSelections,
              severitySelections,
              timingSelections,
              updateTaxonGroupScope,
              updateTaxonGroupSeverity,
              updateTaxonGroupTiming,
              isTaxonScopeOptions,
              isTaxonSeverityOptions,
              isTimingOptions,
              getChecked: (item) => selectedIds.has(item.id),
              onToggleNode: handleToggleNode,
              getDepth: (item) => item.depth || 0,
              onOpenDetails: openDetailsModal,
              onClone: handleCloneNode,
              onDelete: handleDeleteNode,
              monitorThreatId: preselectedItems?.threatItemId || null,
              preSelectedAllItems: filteredAllItems,
              timingAllSelections: timingAllSelections,
              isEditable: isEditable
            }}
            contentComponent={EcosystemAccordionContent}
            contentProps={{
              expandedSpeciesId,
              onToggleExpandedSpecies: handleToggleExpandedSpecies,
              scopeSelections,
              severitySelections,
              timingSelections,
              updateScope,
              updateSeverity,
              updateTiming,
              isScopeOptions,
              isSeverityOptions,
              isTimingOptions,
              isSpeciesView: typeFilter && typeFilter.value === 'species',
              getChecked: (item) => selectedIds.has(item.id),
              onToggleNode: handleToggleNode,
              getDepth: (item) => item.depth || 0,
              onOpenDetails: openDetailsModal,
              onClone: handleCloneNode,
              onDelete: handleDeleteNode,
              isEditable: isEditable
            }}
          />
        )}
      </SpeciesList>
      {filteredPageItems.length > 0 && (
        <PaginationWrapper>
          <ItemsPerPageDropdown
            resultsPerPage={pagination.resultsPerPage}
            totalNumber={totalItems}
            currentPage={pagination.currentPage}
            onChange={(option) => {
              const next = { ...pagination, resultsPerPage: option.value, currentPage: 1 }
              setPagination(next)
              if (serverPaginationProvided) {
                try {
                  document.dispatchEvent(
                    new CustomEvent('species:paginationChanged', {
                      detail: { currentPage: 1, resultsPerPage: option.value }
                    })
                  )
                } catch (e) {}
              }
            }}
          />
          {totalPages > 1 && (
            <Pagination
              activePages={pagination.currentPage}
              numberOfPages={totalPages}
              nextPage={() => {
                const nextPage = Math.min(totalPages, pagination.currentPage + 1)
                setPagination((prev) => ({ ...prev, currentPage: nextPage }))
                if (serverPaginationProvided) {
                  try {
                    document.dispatchEvent(
                      new CustomEvent('species:paginationChanged', {
                        detail: { currentPage: nextPage, resultsPerPage: pagination.resultsPerPage }
                      })
                    )
                  } catch (e) {}
                }
              }}
              prevPage={() => {
                const prevPage = Math.max(1, pagination.currentPage - 1)
                setPagination((prev) => ({ ...prev, currentPage: prevPage }))
                if (serverPaginationProvided) {
                  try {
                    document.dispatchEvent(
                      new CustomEvent('species:paginationChanged', {
                        detail: { currentPage: prevPage, resultsPerPage: pagination.resultsPerPage }
                      })
                    )
                  } catch (e) {}
                }
              }}
              selectPage={(page) => {
                setPagination((prev) => ({ ...prev, currentPage: page }))
                if (serverPaginationProvided) {
                  try {
                    document.dispatchEvent(
                      new CustomEvent('species:paginationChanged', {
                        detail: { currentPage: page, resultsPerPage: pagination.resultsPerPage }
                      })
                    )
                  } catch (e) {}
                }
              }}
            />
          )}
        </PaginationWrapper>
      )}
      <Container>
      <Modal
        width="496px"
        isOpen={isModalForBulkDeletingOpen}
        toggleModal={setIsModalForBulkDeletingOpen}
        modalTitleId={'bulkDeleteModalTitle'}
        bottomBorder={true}
        sideModal={false}
      >
        <FlowRequestModal
          cancelHandler={() => {
            setIsModalForBulkDeletingOpen(false)
          }}
          confirmHandler={bulkDeleteHandler}
          confirmBtnText="yesDelete"
          text={<FormattedMessage id="bulkSpeciesDeleteModalMessage" />}
          item={''}
          approve={false}
        />
      </Modal>
      </Container>
    </div>
  )
}
export default SpeciesTabContent
